/**
 * cl-core/forbidden — 금지 문자열 (지시서 §1-7 · 웹 템플릿 필드와 생성 콘텐츠 공통). 순수 TS.
 *
 * 금지: 미확인 · 확인 중 · 확인필요 · 해당 없음 · 단독 「-」「—」 칸 · 말줄임 「…」 ·
 *       NULL/null/undefined/NaN · 소수 3자리 이상 면적 · snake_case 내부 필드명
 * 예외: 일정 칸의 「미정」 · 「공고 후 안내」
 * 대상 아님: 블로그 본문 원문(호출부가 넣지 않는다).
 *
 * ⚠️ 문장 금지 표현(「알아보겠습니다」 등 · CL fix_2)은 여기 없다 — 레인 D content-gates 가 가진다.
 *    여기는 «사실 칸이 비었음을 드러내는 찌꺼기» 만 본다.
 */

export interface ForbiddenRule {
  id: string;
  /** 사람이 읽는 설명 */
  label: string;
  re: RegExp;
}

/** 본문·필드 공통 규칙(문자열 어디에 있어도 금지). */
export const FORBIDDEN_RULES: readonly ForbiddenRule[] = [
  { id: 'unconfirmed', label: '미확인', re: /미확인/ },
  { id: 'checking', label: '확인 중', re: /확인\s?중/ },
  { id: 'need_check', label: '확인필요', re: /확인\s?필요/ },
  { id: 'not_applicable', label: '해당 없음', re: /해당\s?없음/ },
  { id: 'ellipsis', label: '말줄임', re: /…|\.\.\./ },
  { id: 'null_token', label: 'NULL·undefined·NaN', re: /(^|[^A-Za-z0-9_])(NULL|null|undefined|NaN)(?![A-Za-z0-9_])/ },
  { id: 'area_3dp', label: '소수 3자리 이상 면적', re: /\d+\.\d{3,}\s*(㎡|m²|m2|평)/ },
  { id: 'snake_case', label: 'snake_case 필드명', re: /(^|[^A-Za-z0-9_/.\-])[a-z][a-z0-9]*_[a-z0-9_]*[a-z0-9](?![A-Za-z0-9_])/ },
];

/** 칸 전체가 이것뿐이면 금지(단독 대시 칸). */
const LONE_DASH = /^\s*[-—–―]+\s*$/;

/** 일정 칸에서만 허용되는 값. 다른 칸(사실 표)에서는 빈 값으로 본다. */
export const SCHEDULE_ONLY_VALUES: readonly string[] = ['미정', '공고 후 안내'];

export type FieldKind = 'text' | 'fact' | 'schedule';

export interface ForbiddenHit {
  id: string;
  label: string;
  match: string;
}

/** 문자열에서 금지 규칙 위반을 모두 찾는다. 비어 있으면 []. */
export function findForbidden(value: unknown, kind: FieldKind = 'text'): ForbiddenHit[] {
  if (value == null) return kind === 'text' ? [] : [{ id: 'empty', label: '빈 값', match: '' }];
  const s = String(value);
  const hits: ForbiddenHit[] = [];
  if (kind !== 'text') {
    if (!s.trim()) hits.push({ id: 'empty', label: '빈 값', match: '' });
    if (LONE_DASH.test(s)) hits.push({ id: 'lone_dash', label: '단독 대시 칸', match: s.trim() });
    if (kind === 'fact' && SCHEDULE_ONLY_VALUES.indexOf(s.trim()) >= 0) {
      hits.push({ id: 'schedule_only', label: '일정 칸 전용 값', match: s.trim() });
    }
  }
  for (const r of FORBIDDEN_RULES) {
    const m = r.re.exec(s);
    if (m) hits.push({ id: r.id, label: r.label, match: m[0].trim() });
  }
  return hits;
}

/** 위반이 없으면 true. */
export function isCleanValue(value: unknown, kind: FieldKind = 'text'): boolean {
  return findForbidden(value, kind).length === 0;
}

/**
 * 표 칸 값 정리. 숫자는 문자열로, 위반이면 null(호출부가 «행을 통째로 생략»).
 * ⛔ 빈칸을 「-」 나 「미확인」 으로 메우지 않는다 — 생략이 정답이다.
 */
export function cleanCell(value: unknown, kind: Exclude<FieldKind, 'text'> = 'fact'): string | null {
  if (value == null) return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const s = String(value).replace(/\s+/g, ' ').trim();
  if (!s) return null;
  return isCleanValue(s, kind) ? s : null;
}
